import { useCallback, useMemo, useState } from 'react';

import { TFunction } from '@lyearn/i18n';
import { getDefaultSortValue } from '@/components/FilterManager/SortMenu/utils';
import useFilterManagerWithState from '@/components/FilterManager/useFilterManagerWithState';
import { useFeatureFlags } from '@/helper/FeatureFlags';
import { useAppConfig } from '@/hooks/useAppConfig';
import useEntitySet from '@/hooks/useEntitySet';
import useContents from '@/modules/corporate/hooks/useContents';
import {
  getSortAndFilterItems as getCollectionsSortAndFilterItems,
  GROUP_SORT_DEFAULT_VALUE,
} from '@/modules/corporate/modules/group/views/GroupManager/utils';
import useEvents from '@/modules/events/hooks/useEvents';
import {
  EventStatusEnum,
  EventTypeEnum,
  GroupStatusEnum,
  GroupTypeEnum,
  ResourceStatusEnum,
} from '@/types/schema';

import { getDefaultSort } from '../../modules/admin/views/LearningContent/helpers/getDefaultFilters';
import { LearningContentPageFilters } from '../../modules/admin/views/LearningContent/types';
import useGroups from '../../modules/group/views/GroupManager/hooks/useGroups';
import useSessionSelection from './hooks/useSessionSelection';
import { ContentsDialogProps, Tabs } from './types';
import {
  DEFAULT_DIALOG_CONFIG,
  getContentSortAndFilterItems,
  getSessionSortAndFilterItems,
  getSessionSortItems,
  getTabs,
  OVERRIDES,
} from './utils';

const PAGE_FILTER = LearningContentPageFilters.ACTIVE;

const typesMap = new Map([
  [EventTypeEnum.Live, [EventTypeEnum.Live, EventTypeEnum.RecurringLive]],
  [EventTypeEnum.External, [EventTypeEnum.External, EventTypeEnum.RecurringExternal]],
  [EventTypeEnum.Offline, [EventTypeEnum.Offline, EventTypeEnum.RecurringOffline]],
]);

export default function useContentsDialog({
  t,
  onAddItems,
  isAlreadySelected,
  selectedIds,
  dialogConfigs,
  shareable,
}: {
  t: TFunction;
  selectedIds: ContentsDialogProps['selectedIds'];
  isAlreadySelected: boolean;
  onAddItems: ContentsDialogProps['onAddItems'];
  dialogConfigs: ContentsDialogProps['dialogConfigs'];
  shareable?: boolean;
}) {
  const { selectedContentIds = [], selectedCollectionIds = [], eventSelections = [] } = selectedIds;

  const [inViewTab, setInViewTab] = useState(dialogConfigs?.tabConfig?.[0] ?? Tabs.Content);

  const {
    eventStatus,
    contentTypes,
    tabConfig,
    contentQueryIntent,
    groupQueryIntent,
    recurringCategories,
  } = Object.assign({}, DEFAULT_DIALOG_CONFIG, dialogConfigs);

  const appConfig = useAppConfig();
  const featureFlags = useFeatureFlags();

  const contentSortAndFilterItems = getContentSortAndFilterItems(t, appConfig, featureFlags);
  const collectionSortAndFilterItems = getCollectionsSortAndFilterItems(t);
  const sessionSortAndFilterItems = getSessionSortAndFilterItems(t, appConfig);
  const filterManager = useFilterManagerWithState({});

  const { sort, filter, search } = filterManager.state;

  const contents = useContents({
    otherFilters: {
      status: [ResourceStatusEnum.Published, ResourceStatusEnum.UnpublishedChanges],
      ...filter,
      intent: contentQueryIntent,
    },
    sort: sort.length ? sort : getDefaultSort(PAGE_FILTER),
    searchString: search,
    types: contentTypes,
    pause: !tabConfig.includes(Tabs.Content) || inViewTab !== Tabs.Content,
    shareable,
  });
  const contentsSet = useEntitySet(isAlreadySelected ? [] : selectedContentIds);

  const sessionFilterType = useMemo(() => {
    if (!filter.types) return undefined;

    return (filter.types as EventTypeEnum[]).reduce((result, type) => {
      return [...result, ...(typesMap.get(type) ?? [])];
    }, [] as EventTypeEnum[]);
  }, [filter.types]);

  const sessions = useEvents({
    filters: {
      name: search,
      shareable,
      ...filter,
      types: sessionFilterType,
      status: filter.status ? (filter.status as EventStatusEnum[]) : eventStatus,
      recurringCategories,
    },
    sort: sort.length ? sort : getDefaultSortValue(getSessionSortItems(t)),
    pause: !tabConfig.includes(Tabs.Sessions) || inViewTab !== Tabs.Sessions,
  });
  const { childSessionsMap, getAssignmentType, ...eventSelectionsSet } = useSessionSelection({
    eventSelections: isAlreadySelected ? [] : eventSelections,
  });

  const collections = useGroups({
    filters: {
      ...filter,
      name: search,
      systemGenerated: false,
      status: [GroupStatusEnum.Published],
      types: [GroupTypeEnum.ContentCollection],
      shareable,
      intent: groupQueryIntent,
    },
    sort: sort.length ? sort : GROUP_SORT_DEFAULT_VALUE,
    pause: !tabConfig.includes(Tabs.Collections) || inViewTab !== Tabs.Collections,
  });
  const collectionsSet = useEntitySet(isAlreadySelected ? [] : selectedCollectionIds);

  const tabs = useMemo(() => {
    return {
      value: inViewTab,
      tabs: getTabs(t, tabConfig),
      onChange: (val: string) => {
        filterManager.methods.clearFilters();
        filterManager.methods.clearSort();
        setInViewTab(val as Tabs);
      },
      overrides: OVERRIDES,
    };
  }, [filterManager.methods, inViewTab, t, tabConfig]);

  const data = useMemo(() => {
    switch (inViewTab) {
      case Tabs.Content:
        return {
          adaptedContents: contents.contents,
          ...contents,
          ...contentsSet,
          error: contents.error,
        };
      case Tabs.Sessions:
        return {
          adaptedContents: sessions.events,
          ...sessions,
          ...eventSelectionsSet,
          error: sessions.error,
        };
      default:
        return {
          adaptedContents: collections.groups,
          ...collections,
          ...collectionsSet,
          error: collections.error,
        };
    }
  }, [collections, collectionsSet, contents, contentsSet, eventSelectionsSet, inViewTab, sessions]);

  const sortAndFilterItems = useMemo(() => {
    switch (inViewTab) {
      case Tabs.Content:
        return contentSortAndFilterItems;
      case Tabs.Collections:
        return collectionSortAndFilterItems;
      case Tabs.Sessions:
        return sessionSortAndFilterItems;
      default:
        return undefined;
    }
  }, [
    collectionSortAndFilterItems,
    contentSortAndFilterItems,
    inViewTab,
    sessionSortAndFilterItems,
  ]);

  const onAddClick = useCallback(
    () =>
      onAddItems({
        selectedContentIds: Array.from(contentsSet.selectedItemsSet),
        selectedCollectionIds: Array.from(collectionsSet.selectedItemsSet),
        eventSelections: Array.from(eventSelectionsSet.selectedItemsSet),
      }),
    [
      collectionsSet.selectedItemsSet,
      contentsSet.selectedItemsSet,
      eventSelectionsSet.selectedItemsSet,
      onAddItems,
    ],
  );

  const selectedCount =
    contentsSet.selectedItemsSet.size +
    collectionsSet.selectedItemsSet.size +
    eventSelectionsSet.selectedItemsSet.size;

  return {
    onAddClick,
    data,
    tabs,
    searchInput: search,
    setSearchStr: filterManager.methods.setSearch,
    debouncedSearchStr: search,
    inViewTab,
    selectedCount,
    filterManager,
    sortAndFilterItems,
    childSessionsMap,
    getAssignmentType,
  };
}
